import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, WifiHigh, ArrowCounterClockwise,
  Warning, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListDevices, adminCreateDevice, adminUpdateDevice, adminDeleteDevice,
  adminRestoreDevice, adminHardDeleteDevice, adminListAllDevices,
  adminListRooms, adminListPatients, adminListOrgs,
  adminListRoomsForOrg, adminListPatientsForOrg, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, monoCls, selectCls, Modal } from '../../components/admin/Field';
import { loadDeviceCatalog, deviceLabelFrom, categoriesFrom, categoryLabel, catalogEntry } from '../../lib/deviceCatalog';
import { isImeiType, isValidImei, cleanImei } from '../../lib/imei';

const PAGE_SIZES = [10, 25, 50, 100];

// Super-admin-only 3-way view switch: Active · Archived · Full list.
function ViewSwitch({ value, onChange }) {
  const { t } = useI18n();
  const opts = [
    { v: 'active', label: t('admin_view_active') },
    { v: 'archived', label: t('admin_view_archived') },
    { v: 'full', label: t('admin_view_full') },
  ];
  return (
    <div data-testid="admin-devices-view-switch" role="tablist"
      className="inline-flex items-center gap-1 bg-bg-alt border border-stone_line rounded-full p-1">
      {opts.map((o) => (
        <button key={o.v} type="button" role="tab" aria-selected={value === o.v}
          onClick={() => onChange(o.v)}
          data-testid={`admin-devices-view-${o.v}`}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            value === o.v ? 'bg-white text-ink shadow-sm' : 'text-ink-soft hover:text-ink'
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function AdminDevices() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current, isSuperAdmin } = useOrg();

  // 'active' | 'archived' | 'full' (full is super-admin only).
  const [viewMode, setViewMode] = useState('active');

  // Per-org data (active/archived modes).
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [patients, setPatients] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Full-list data + server-side filters.
  const [fullItems, setFullItems] = useState([]);
  const [fullTotal, setFullTotal] = useState(0);
  const [fullLoading, setFullLoading] = useState(false);
  const [assignment, setAssignment] = useState('all');   // all|patient|room|unassigned
  const [statusFilter, setStatusFilter] = useState('all'); // all|active|archived
  const [orgFilter, setOrgFilter] = useState('');          // '' = all orgs
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [typeFilter, setTypeFilter] = useState('all');
  const [catalog, setCatalog] = useState([]);

  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const allowed = user?.role === 'admin';
  const isFull = viewMode === 'full';
  const isArchived = viewMode === 'archived';

  // Debounce the search box (used server-side in full mode).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Reset to page 1 whenever a full-list filter changes.
  useEffect(() => { setPage(1); }, [debouncedQ, assignment, statusFilter, orgFilter, typeFilter, pageSize, viewMode]);

  // ---- Per-org list (active / archived) ----
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ items: devs }, r, p] = await Promise.all([
        adminListDevices({ archived: isArchived }),
        adminListRooms().catch(() => ({ items: [] })),
        adminListPatients().catch(() => ({ items: [] })),
      ]);
      const filtered = isArchived ? devs.filter((d) => d.deleted_at) : devs;
      setItems(filtered);
      setRooms(r.items);
      setPatients(p.items);
    } catch (err) {
      toast.error(formatAdminError('List devices', err));
    } finally {
      setLoading(false);
    }
  }, [isArchived]);

  // ---- Cross-org list (full) ----
  const reloadFull = useCallback(async () => {
    setFullLoading(true);
    try {
      const res = await adminListAllDevices({
        q: debouncedQ, assignment, status: statusFilter, type: typeFilter,
        organization_id: orgFilter || undefined, page, page_size: pageSize,
      });
      setFullItems(res.items);
      setFullTotal(res.total);
    } catch (err) {
      toast.error(formatAdminError('List all devices', err));
      setFullItems([]);
      setFullTotal(0);
    } finally {
      setFullLoading(false);
    }
  }, [debouncedQ, assignment, statusFilter, orgFilter, typeFilter, page, pageSize]);

  const reloadCurrent = useCallback(() => (isFull ? reloadFull() : reload()), [isFull, reloadFull, reload]);

  useEffect(() => {
    if (!allowed) return;
    if (isFull) reloadFull();
    else reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, viewMode, reload, reloadFull]);

  // Orgs catalog for the picker + org filter (super-admin only).
  useEffect(() => {
    if (!allowed || !isSuperAdmin) return;
    adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([]));
  }, [allowed, isSuperAdmin]);

  // Device-type catalog (source of truth for models/labels/wearable).
  useEffect(() => {
    if (!allowed) return;
    loadDeviceCatalog().then(setCatalog).catch(() => setCatalog([]));
  }, [allowed]);

  // Non-full search is client-side.
  const clientFiltered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((d) => (d.mac || '').toLowerCase().includes(needle) || (d.type || '').toLowerCase().includes(needle));
  }, [items, q]);

  const orgName = useCallback((id) => orgs.find((o) => o.id === id)?.name || id || '—', [orgs]);

  // ---- Mutations (orgId override only needed in full/cross-org mode) ----
  const onSave = async (form) => {
    setBusy(true);
    try {
      if (editing === 'new') {
        await adminCreateDevice(form, isFull ? form.organization_id : undefined);
        toast.success(t('admin_device_created'));
      } else {
        // In full mode, target the device's ORIGINAL org so the backend can move it.
        await adminUpdateDevice(editing.id, form, isFull ? editing.organization_id : undefined);
        toast.success(t('admin_device_updated'));
      }
      setEditing(null);
      reloadCurrent();
    } catch (err) {
      // Surface backend 422 assignment-rule messages verbatim.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : formatAdminError(editing === 'new' ? 'Create device' : 'Update device', err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteDevice(confirm.id, isFull ? confirm.organization_id : undefined);
      toast.success(t('admin_device_deleted'));
      setConfirm(null);
      reloadCurrent();
    } catch (err) {
      toast.error(formatAdminError('Delete device', err));
    } finally { setBusy(false); }
  };

  const onRestore = async (d) => {
    setBusy(true);
    try {
      await adminRestoreDevice(d.id, isFull ? d.organization_id : undefined);
      toast.success(t('admin_restored'));
      reloadCurrent();
    } catch (err) {
      toast.error(formatAdminError('Restore device', err));
    } finally { setBusy(false); }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteDevice(hardConfirm.id, isFull ? hardConfirm.organization_id : undefined);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reloadCurrent();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete device', err));
    } finally { setBusy(false); }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_devices_title')}>
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  const rows = isFull ? fullItems : clientFiltered;
  const listLoading = isFull ? fullLoading : loading;
  const totalPages = Math.max(1, Math.ceil(fullTotal / pageSize));

  const assignmentOf = (d) => {
    // Full mode: names come from the endpoint. Per-org: resolve from loaded lists.
    const patientName = isFull ? d.assigned_patient_name : patients.find((p) => p.id === d.assigned_patient_id)?.name;
    const roomName = isFull ? d.assigned_room_name : rooms.find((r) => r.id === d.assigned_room_id)?.room_name;
    if (!patientName && !roomName) return '—';
    return [patientName ? `${t('admin_field_patient')}: ${patientName}` : null,
      roomName ? `${t('admin_field_room')}: ${roomName}` : null].filter(Boolean).join(' · ');
  };

  return (
    <AdminPage
      title={t('admin_devices_title')}
      description={isSuperAdmin ? t('admin_devices_sub_super') : t('admin_devices_sub_org', { org: current?.name || '—' })}
    >

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input type="text" placeholder={t('search')} value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-devices-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone_line bg-white text-sm focus:outline-none focus:border-sage" />
        </div>
        {!isArchived && !isFull && (
          <button type="button" onClick={() => setEditing('new')} data-testid="admin-device-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700">
            <Plus size={16} weight="bold" /> {t('admin_device_new')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <ViewSwitch value={viewMode} onChange={setViewMode} />

          {isFull && (
            <>
              <select value={assignment} onChange={(e) => setAssignment(e.target.value)}
                data-testid="admin-devices-assignment-filter" className={`${selectCls} !w-auto text-xs py-1.5`}
                aria-label={t('device_filter_assignment')}>
                <option value="all">{t('device_filter_assignment')}: {t('all')}</option>
                <option value="patient">{t('device_filter_patient')}</option>
                <option value="room">{t('device_filter_room')}</option>
                <option value="unassigned">{t('device_filter_unassigned')}</option>
              </select>

              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="admin-devices-status-filter" className={`${selectCls} !w-auto text-xs py-1.5`}
                aria-label={t('admin_field_status')}>
                <option value="all">{t('admin_field_status')}: {t('all')}</option>
                <option value="active">{t('admin_view_active')}</option>
                <option value="archived">{t('admin_view_archived')}</option>
              </select>

              <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                data-testid="admin-devices-org-filter" className={`${selectCls} !w-auto text-xs py-1.5`}
                aria-label={t('admin_field_organization')}>
                <option value="">{t('admin_all_orgs')}</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>

              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="admin-devices-type-filter" className={`${selectCls} !w-auto text-xs py-1.5`}
                aria-label={t('admin_field_type')}>
                <option value="all">{t('admin_field_type')}: {t('all')}</option>
                {categoriesFrom(catalog).map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {listLoading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {isArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-devices-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_field_type')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_mac')}</th>
                {isFull && <th className="px-5 py-3 font-medium">{t('admin_field_organization')}</th>}
                <th className="px-5 py-3 font-medium">{t('admin_field_assignment')}</th>
                {isFull && <th className="px-5 py-3 font-medium">{t('admin_field_status')}</th>}
                {!isFull && <th className="px-5 py-3 font-medium">{t('admin_field_firmware')}</th>}
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {rows.map((d) => {
                const archivedRow = !!d.deleted_at;
                return (
                  <tr key={d.id} className={`hover:bg-bg-alt/40 ${archivedRow ? 'opacity-70' : ''}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                          <WifiHigh size={16} weight="duotone" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-ink">{deviceLabelFrom(catalog, d.type, t)}</div>
                          {catalogEntry(catalog, d.type)?.category === 'door_sensor' && ('sensor_target' in d) && (
                            <div className="text-[11px] text-ink-soft truncate">
                              {d.sensor_target
                                ? `${t(`sensor_target_${d.sensor_target}`)}${d.sensor_label ? ` · ${d.sensor_label}` : ''}`
                                : <span className="text-amber_warn">{t('sensor_target_unset')}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-soft font-mono text-xs">{d.mac || '—'}</td>
                    {isFull && (
                      <td className="px-5 py-3 text-ink-soft text-xs" data-testid={`admin-device-org-name-${d.id}`}>
                        {d.organization_name || orgName(d.organization_id)}
                      </td>
                    )}
                    <td className="px-5 py-3 text-ink-soft text-xs">{assignmentOf(d)}</td>
                    {isFull && (
                      <td className="px-5 py-3">
                        <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full ${
                          archivedRow ? 'bg-stone_line text-ink-soft' : 'bg-sage-100 text-sage-700'
                        }`}>
                          {archivedRow ? t('admin_view_archived') : t('status_active')}
                        </span>
                      </td>
                    )}
                    {!isFull && <td className="px-5 py-3 text-ink-soft font-mono text-xs">{d.firmware_version || '—'}</td>}
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {archivedRow ? (
                        <>
                          <button onClick={() => onRestore(d)} disabled={busy}
                            data-testid={`admin-device-restore-${d.id}`}
                            className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 mr-1 disabled:opacity-50"
                            aria-label={t('admin_restore')} title={t('admin_restore')}>
                            <ArrowCounterClockwise size={16} />
                          </button>
                          <button onClick={() => setHardConfirm(d)} disabled={busy}
                            data-testid={`admin-device-hard-delete-${d.id}`}
                            className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                            aria-label={t('admin_hard_delete')} title={t('admin_hard_delete')}>
                            <Warning size={16} weight="bold" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditing(d)} data-testid={`admin-device-edit-${d.id}`}
                            className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink mr-1" aria-label={t('edit')}>
                            <PencilSimple size={16} />
                          </button>
                          <button onClick={() => setConfirm(d)} data-testid={`admin-device-delete-${d.id}`}
                            className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700" aria-label={t('admin_delete')}>
                            <Trash size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination (full mode) */}
      {isFull && !listLoading && (
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap text-xs text-ink-soft">
          <div data-testid="admin-devices-total">{t('pagination_total', { total: fullTotal })}</div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              {t('pagination_per_page')}
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                data-testid="admin-devices-page-size" className={`${selectCls} !w-auto py-1`}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              data-testid="admin-devices-page-prev"
              className="p-1.5 rounded-lg border border-stone_line hover:bg-bg-alt disabled:opacity-40">
              <CaretLeft size={14} />
            </button>
            <span data-testid="admin-devices-page-indicator">{t('pagination_page_of', { page, pages: totalPages })}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              data-testid="admin-devices-page-next"
              className="p-1.5 rounded-lg border border-stone_line hover:bg-bg-alt disabled:opacity-40">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      )}

      {editing && (
        <DeviceForm
          device={editing === 'new' ? null : editing}
          rooms={rooms}
          patients={patients}
          orgs={orgs}
          catalog={catalog}
          showOrgPicker={isSuperAdmin}
          activeOrg={current}
          orgScopedFetch={isFull}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_device_delete_title')}
        description={confirm ? t('admin_device_delete_confirm', { mac: confirm.mac || '?' }) : ''}
        confirmLabel={t('admin_delete')}
        danger busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.mac || '?' }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function DeviceForm({ device, rooms: roomsProp, patients: patientsProp, orgs, catalog = [], showOrgPicker, activeOrg, orgScopedFetch, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    type: device?.type || catalog[0]?.value || '',
    mac: device?.mac || '',
    name: device?.name || '',
    firmware: device?.firmware || '',
    assigned_room_id: device?.assigned_room_id || '',
    assigned_patient_id: device?.assigned_patient_id || '',
    sensor_target: device?.sensor_target || '',
    sensor_label: device?.sensor_label || '',
    organization_id: device?.organization_id || activeOrg?.id || '',
  });

  // Cross-org (full-list) editing: load rooms/patients for the SELECTED org so
  // the admin can reassign within any tenant. Refetches when org changes.
  const [scopedRooms, setScopedRooms] = useState([]);
  const [scopedPatients, setScopedPatients] = useState([]);
  const [scopedLoading, setScopedLoading] = useState(false);

  useEffect(() => {
    if (!orgScopedFetch || !form.organization_id) { setScopedRooms([]); setScopedPatients([]); return; }
    let cancelled = false;
    setScopedLoading(true);
    Promise.all([
      adminListRoomsForOrg(form.organization_id).catch(() => []),
      adminListPatientsForOrg(form.organization_id).catch(() => []),
    ]).then(([r, p]) => {
      if (!cancelled) { setScopedRooms(r); setScopedPatients(p); }
    }).finally(() => { if (!cancelled) setScopedLoading(false); });
    return () => { cancelled = true; };
  }, [orgScopedFetch, form.organization_id]);

  const rooms = orgScopedFetch ? scopedRooms : roomsProp;
  const patients = orgScopedFetch ? scopedPatients : patientsProp;
  // Resolve the catalog entry by concrete model value (e.g. "w3pro"). If the
  // backend still returns a CATEGORY name (e.g. "bracelet") instead of a model,
  // fall back to matching a category so we can still pick the correct assignment
  // control. Everything is derived from the catalog — no hardcoded type aliases.
  const entry = catalogEntry(catalog, form.type);
  const cats = categoriesFrom(catalog);
  const category = entry?.category || (cats.includes(form.type) ? form.type : null);
  const categoryModels = category ? catalog.filter((d) => d.category === category) : [];
  // wearable => assign to a patient, else to a room (never both). Unknown types
  // with no catalog match degrade to non-wearable (room) assignment.
  const wearable = entry
    ? !!entry.wearable
    : (categoryModels.length > 0 && categoryModels.every((d) => d.wearable));
  const isDoorSensor = category === 'door_sensor';
  // A motion sensor can also belong to one resident (e.g. aimed at their bed).
  const isMotion = form.type === 'motion_sensor' || category === 'motion_sensor';
  const roomResidents = patients.filter((p) => (p.room_id || p.assigned_room_id) === form.assigned_room_id);
  const imeiMode = isImeiType(form.type);
  const [imeiErr, setImeiErr] = useState('');
  // Disable assignment only when we CAN'T load the target org's data
  // (i.e. a per-org edit that moves the device to another org).
  const isCrossOrg = !orgScopedFetch && showOrgPicker && !!form.organization_id && !!activeOrg?.id && form.organization_id !== activeOrg.id;
  const assignDisabled = isCrossOrg || (orgScopedFetch && scopedLoading);

  const submit = (e) => {
    e.preventDefault();
    if (!form.mac.trim()) return;
    if (imeiMode && !isValidImei(form.mac)) {
      setImeiErr(cleanImei(form.mac).length === 15 ? t('imei_invalid') : t('imei_length'));
      return;
    }
    const body = {
      type: form.type,
      mac: imeiMode ? cleanImei(form.mac) : form.mac.trim(),
      name: form.name?.trim() || null,
      firmware: form.firmware || null,
      sensor_target: isDoorSensor ? (form.sensor_target || null) : null,
      sensor_label: isDoorSensor ? (form.sensor_label || null) : null,
    };
    // Single assignment based on wearable — backend 422s if both are set.
    if (wearable) {
      body.assigned_patient_id = form.assigned_patient_id || null;
      body.assigned_room_id = null;
    } else {
      body.assigned_room_id = form.assigned_room_id || null;
      body.assigned_patient_id = isMotion && form.assigned_room_id ? (form.assigned_patient_id || null) : null;
    }
    if (showOrgPicker && form.organization_id) body.organization_id = form.organization_id;
    onSave(body);
  };

  return (
    <Modal onCancel={onCancel} wide>
      <form onSubmit={submit} data-testid="admin-device-form" className="space-y-4">
        <h2 className="font-heading text-2xl text-ink">
          {device ? t('admin_device_edit_title') : t('admin_device_new_title')}
        </h2>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_type')} required>
            <select value={form.type} disabled={!!device && imeiMode}
              onChange={(e) => { setForm({ ...form, type: e.target.value, mac: '', assigned_room_id: '', assigned_patient_id: '' }); setImeiErr(''); }}
              data-testid="admin-device-type" className={`${selectCls} disabled:opacity-60`}>
              {/* Degrade gracefully: if the current type isn't in the catalog
                  (e.g. unknown model before a catalog refresh), show it raw. */}
              {form.type && !catalogEntry(catalog, form.type) && (
                <option value={form.type}>{form.type}</option>
              )}
              {categoriesFrom(catalog).map((cat) => (
                <optgroup key={cat} label={categoryLabel(cat)}>
                  {catalog.filter((d) => d.category === cat).map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label={imeiMode ? t('imei_label') : t('admin_field_mac')} required hint={imeiMode ? t('imei_hint') : undefined}>
            {imeiMode ? (
              <input type="text" required value={form.mac} inputMode="numeric" maxLength={15}
                onChange={(e) => { setForm({ ...form, mac: cleanImei(e.target.value).slice(0, 15) }); setImeiErr(''); }}
                onBlur={() => { if (form.mac && !isValidImei(form.mac)) setImeiErr(cleanImei(form.mac).length === 15 ? t('imei_invalid') : t('imei_length')); }}
                data-testid="admin-device-mac"
                placeholder="862061045678918" className={monoCls} />
            ) : (
              <input type="text" required value={form.mac}
                onChange={(e) => setForm({ ...form, mac: e.target.value.toUpperCase() })}
                data-testid="admin-device-mac"
                placeholder="AA:BB:CC:DD:EE:FF" className={monoCls} />
            )}
            {imeiErr && <p className="text-xs text-terracotta-700 mt-1" data-testid="admin-device-imei-error">{imeiErr}</p>}
          </Field>
        </div>

        <Field label={t('admin_field_name')}>
          <input type="text" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="admin-device-name"
            placeholder="Bracelet #4" className={inputCls} />
        </Field>

        <Field label={t('admin_field_firmware')}>
          <input type="text" value={form.firmware}
            onChange={(e) => setForm({ ...form, firmware: e.target.value })}
            data-testid="admin-device-firmware"
            placeholder="v1.0.0" className={monoCls} />
        </Field>

        {/* Organization picker first in cross-org mode so it drives the lists below. */}
        {showOrgPicker && orgs.length > 0 && (
          <Field label={t('admin_field_organization')} hint={isCrossOrg ? t('admin_device_org_change_hint') : undefined}>
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value, assigned_room_id: '', assigned_patient_id: '' })}
              data-testid="admin-device-org" className={selectCls}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
        )}

        <div className="border-t border-stone_line pt-4">
          {wearable ? (
            <Field label={t('admin_field_assigned_patient')} hint={isCrossOrg ? t('admin_device_org_change_hint') : t('admin_field_assigned_hint')}>
              <select value={form.assigned_patient_id} disabled={assignDisabled}
                onChange={(e) => setForm({ ...form, assigned_patient_id: e.target.value })}
                data-testid="admin-device-patient" className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
                <option value="">— {t('device_filter_unassigned')} —</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {imeiMode && <p className="text-xs text-ink-muted mt-1">{t('imei_resident_note')}</p>}
            </Field>
          ) : (
            <Field label={t('admin_field_assigned_room')} hint={isCrossOrg ? t('admin_device_org_change_hint') : t('admin_field_assigned_hint')}>
              <select value={form.assigned_room_id} disabled={assignDisabled}
                onChange={(e) => setForm({ ...form, assigned_room_id: e.target.value, assigned_patient_id: '' })}
                data-testid="admin-device-room" className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
                <option value="">— {t('device_filter_unassigned')} —</option>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.room_name}</option>)}
              </select>
            </Field>
          )}
          {!wearable && isMotion && form.assigned_room_id && (
            <div className="mt-3">
              <Field label={t('admin_motion_resident')} hint={t('admin_motion_resident_hint')}>
                <select value={form.assigned_patient_id} disabled={assignDisabled}
                  onChange={(e) => setForm({ ...form, assigned_patient_id: e.target.value })}
                  data-testid="admin-device-motion-resident" className={`${selectCls} disabled:opacity-50`}>
                  <option value="">— {t('admin_motion_whole_room')} —</option>
                  {roomResidents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
            </div>
          )}
        </div>

        {isDoorSensor && (
          <div className="grid sm:grid-cols-2 gap-3 border-t border-stone_line pt-4 bg-sage-100/40 -mx-6 px-6 -mb-2 pb-4 rounded-b-xl">
            <Field label={t('admin_field_sensor_target')} hint={t('admin_field_sensor_target_hint')} required>
              <select value={form.sensor_target}
                onChange={(e) => setForm({ ...form, sensor_target: e.target.value })}
                data-testid="admin-device-sensor-target" className={selectCls}>
                <option value="">—</option>
                <option value="door">{t('sensor_target_door')}</option>
                <option value="window">{t('sensor_target_window')}</option>
              </select>
            </Field>
            <Field label={t('admin_field_sensor_label')} hint={t('admin_field_sensor_label_hint')}>
              <input type="text" value={form.sensor_label}
                onChange={(e) => setForm({ ...form, sensor_label: e.target.value })}
                data-testid="admin-device-sensor-label"
                placeholder={t('sensor_target_door') + ' / ' + t('sensor_target_window')}
                className={inputCls} />
            </Field>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="admin-device-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.mac.trim()} data-testid="admin-device-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
